import Decimal from 'decimal.js';
import { isDateAfter, isDateBefore, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { eventType } from 'src/service-worker/offline_handlers/aol/enums';
import { CargoInTankItem, CargoTempCategory } from 'src/types/ViewModel/CargoTempLog/CargoInTankItem';
import { CargoTemperatureLogDate } from 'src/types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { getFormattedCargoNumber } from '../stowage/cargoOperationUtils';
import { getCurrentPortCall } from '../stowage/stowageRotationUtils';
import { CargoTemperatureConfig } from 'src/types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { MarpolRegulationEnum } from 'src/types/BusinessModel/Stowage/Product/MarpolRegulationEnum';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { ProductCategory } from 'src/types/ViewModel/Stowage/Product/ProductCategory';

/** IBC chapter constants for cargo temperature categorization */
export const IBC_CHAPTERS = {
    INHIBITED: 'IBC-15.13',
    HEAT_SENSITIVE: 'IBC-16.6',
    COOLED: 'IBC-15.14',
    HEATED: 'IBC-16.2',
} as const;

/**
 * Determines the cargo temperature category based on IBC chapter assignments (Annex II)
 * or voyage temperature ranges (Annex I).
 *
 * Priority order: Inhibited > Heat Sensitive > Cooled > Heated > Non-Heat
 *
 * @see US #24613 - New Cargo Temperature Categorization
 */
export const isCargoTempDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.localStorage?.getItem('orcaweb:cargo-temp-debug') === '1';
};

export const cargoTempDebugLog = (message: string, payload?: unknown): void => {
    if (!isCargoTempDebugEnabled()) return;
    if (payload === undefined) {
        console.info(`[CargoTempDebug] ${message}`);
        return;
    }
    console.info(`[CargoTempDebug] ${message}`, payload);
};

export enum CargoTempCategoryReasonCode {
    MissingProduct = 'missingProduct',
    InhibitedByIbc1513 = 'inhibitedByIbc1513',
    HeatSensitiveByIbc166 = 'heatSensitiveByIbc166',
    CooledByIbc1514 = 'cooledByIbc1514',
    HeatedByAnnexINonStandardRange = 'heatedByAnnexINonStandardRange',
    HeatedByIbc162 = 'heatedByIbc162',
    HeatedByNonStandardRangeFallback = 'heatedByNonStandardRangeFallback',
    NonHeatDefault = 'nonHeatDefault',
}

export interface CargoTempCategoryEvaluationResult {
    category: CargoTempCategory;
    reasonCode: CargoTempCategoryReasonCode;
    reason: string;
    matchedIbcRequirement: string | null;
}

export interface CargoTempCategoryMetadata {
    minTemp: number | null | undefined;
    maxTemp: number | null | undefined;
    isNonStandardMin: boolean;
    isNonStandardMax: boolean;
    marpolRegulation: MarpolRegulationEnum | undefined;
}

const getCargoTempMetadata = (product: Product, category: ProductCategory | null | undefined): CargoTempCategoryMetadata => {
    const minTemp = product.voyageTemperatureRange?.min;
    const maxTemp = product.voyageTemperatureRange?.max;
    const isNonStandardMin = minTemp != null && minTemp !== 0;
    const isNonStandardMax = maxTemp != null && maxTemp !== 35;

    return {
        minTemp,
        maxTemp,
        isNonStandardMin,
        isNonStandardMax,
        marpolRegulation: category?.marpolRegulation,
    };
};

const findMatchingIbcRequirement = (ibcRequirements: readonly string[], chapter: string): string | null => {
    return ibcRequirements.find((req) => req?.startsWith(chapter)) ?? null;
};

export const evaluateCargoTempCategory = (
    product: Product | null | undefined,
    category: ProductCategory | null | undefined
): CargoTempCategoryEvaluationResult => {
    if (!product) {
        return {
            category: CargoTempCategory.NonHeat,
            reasonCode: CargoTempCategoryReasonCode.MissingProduct,
            reason: 'No product data is available, so category defaults to Non-Heat.',
            matchedIbcRequirement: null,
        };
    }

    const resolvedCategory = category ?? product.category;
    const ibcRequirements = resolvedCategory?.ibcSpecificRequirements ?? [];
    const metadata = getCargoTempMetadata(product, resolvedCategory);

    const inhibitedRequirement = findMatchingIbcRequirement(ibcRequirements, IBC_CHAPTERS.INHIBITED);
    if (inhibitedRequirement) {
        return {
            category: CargoTempCategory.Inhibited,
            reasonCode: CargoTempCategoryReasonCode.InhibitedByIbc1513,
            reason: `Matched IBC requirement ${inhibitedRequirement}, which maps to Inhibited.`,
            matchedIbcRequirement: inhibitedRequirement,
        };
    }

    const heatSensitiveRequirement = findMatchingIbcRequirement(ibcRequirements, IBC_CHAPTERS.HEAT_SENSITIVE);
    if (heatSensitiveRequirement) {
        return {
            category: CargoTempCategory.HeatSensitive,
            reasonCode: CargoTempCategoryReasonCode.HeatSensitiveByIbc166,
            reason: `Matched IBC requirement ${heatSensitiveRequirement}, which maps to Heat Sensitive.`,
            matchedIbcRequirement: heatSensitiveRequirement,
        };
    }

    const cooledRequirement = findMatchingIbcRequirement(ibcRequirements, IBC_CHAPTERS.COOLED);
    if (cooledRequirement) {
        return {
            category: CargoTempCategory.Cooled,
            reasonCode: CargoTempCategoryReasonCode.CooledByIbc1514,
            reason: `Matched IBC requirement ${cooledRequirement}, which maps to Cooled.`,
            matchedIbcRequirement: cooledRequirement,
        };
    }

    if (metadata.marpolRegulation === MarpolRegulationEnum.AnnexI && (metadata.isNonStandardMin || metadata.isNonStandardMax)) {
        return {
            category: CargoTempCategory.Heated,
            reasonCode: CargoTempCategoryReasonCode.HeatedByAnnexINonStandardRange,
            reason: `Annex I product has non-standard voyage temperature range (${metadata.minTemp ?? 'n/a'} to ${
                metadata.maxTemp ?? 'n/a'
            } °C), so category is Heated.`,
            matchedIbcRequirement: null,
        };
    }

    const heatedRequirement = findMatchingIbcRequirement(ibcRequirements, IBC_CHAPTERS.HEATED);
    if (heatedRequirement) {
        return {
            category: CargoTempCategory.Heated,
            reasonCode: CargoTempCategoryReasonCode.HeatedByIbc162,
            reason: `Matched IBC requirement ${heatedRequirement}, which maps to Heated.`,
            matchedIbcRequirement: heatedRequirement,
        };
    }

    if (metadata.isNonStandardMin || metadata.isNonStandardMax) {
        return {
            category: CargoTempCategory.Heated,
            reasonCode: CargoTempCategoryReasonCode.HeatedByNonStandardRangeFallback,
            reason: `Voyage temperature range (${metadata.minTemp ?? 'n/a'} to ${metadata.maxTemp ?? 'n/a'} °C) is non-standard, so category is Heated.`,
            matchedIbcRequirement: null,
        };
    }

    return {
        category: CargoTempCategory.NonHeat,
        reasonCode: CargoTempCategoryReasonCode.NonHeatDefault,
        reason: 'No temperature-driving IBC requirement or non-standard voyage range was found, so category is Non-Heat.',
        matchedIbcRequirement: null,
    };
};

export const getCargoTempCategory = (cargo: RotationCargo): CargoTempCategory => {
    return evaluateCargoTempCategory(cargo?.product, cargo?.product?.category).category;
};

/**
 * Derive active cargoes from cargo operations — excludes orphan cargoes
 * that exist in stowagePlan.cargoes but have no associated operations
 * (e.g., old IMOS cargoes replaced after the change tree sync cutoff).
 * This mirrors the stowage page's implicit filtering via the operations hierarchy.
 */
export const getActiveCargoes = (stowagePlan: StowagePlan): RotationCargo[] => {
    if (!stowagePlan?.cargoOperations?.length) return stowagePlan?.cargoes ?? [];
    const activeCargoGuids = new Set(
        stowagePlan.cargoOperations.map((op) => op.cargoGuid ?? op.cargo?.cargoGuid).filter((guid): guid is string => !!guid)
    );
    return stowagePlan.cargoes.filter((c) => activeCargoGuids.has(c.cargoGuid));
};

export const shouldPopulateCargoOnBoardFromStowage = (
    isStowageIntegrationReady: boolean,
    activeMasterPlan: StowagePlan | null | undefined,
    events: TankersEvent[] | null | undefined
): boolean => {
    return isStowageIntegrationReady && !!activeMasterPlan && Array.isArray(events);
};

export const populateCargoOnBoardFromStowage = (
    cargoTempLogDates: CargoTemperatureLogDate[],
    activeMasterPlan: StowagePlan,
    stowageIntegrationFromDate: Date,
    events: TankersEvent[],
    vessel: Vessel
): CargoTemperatureLogDate[] => {
    if (!activeMasterPlan || !activeMasterPlan.cargoes || !activeMasterPlan.stowageItems || !vessel) return cargoTempLogDates;
    cargoTempDebugLog('populateCargoOnBoardFromStowage:start', {
        logDateCount: cargoTempLogDates?.length ?? 0,
        stowageItemsCount: activeMasterPlan.stowageItems?.length ?? 0,
        eventsCount: events?.length ?? 0,
        stowageIntegrationFromDate: stowageIntegrationFromDate?.toISOString?.(),
    });

    const fromDate = stowageIntegrationFromDate;
    const firstVoyage = vessel.firstVoyageOrcaWebStowageImosSync;

    if (!firstVoyage || firstVoyage <= 0) {
        // If first voyage is not set, we cannot determine cargo on board
        return cargoTempLogDates;
    }

    const currentPortCall = activeMasterPlan?.rotation?.length ? getCurrentPortCall(activeMasterPlan) : null;
    const currentVoyage = Number(currentPortCall?.voyageNumber);
    const minimumRelevantVoyage = Number.isFinite(currentVoyage) ? currentVoyage - 1 : firstVoyage;
    const voyageCutoff = Math.max(firstVoyage, minimumRelevantVoyage);
    cargoTempDebugLog('populateCargoOnBoardFromStowage:voyageCutoff', {
        firstVoyage,
        currentVoyage: Number.isFinite(currentVoyage) ? currentVoyage : null,
        voyageCutoff,
    });

    const activeCargoes = getActiveCargoes(activeMasterPlan);
    const unloadCompletionEventTypes = new Set<number>([eventType.pumpingComplete, eventType.cargoHoseOff]);

    cargoTempLogDates.forEach((cargoTempLogDate) => {
        const cargoInTankItems: CargoInTankItem[] = [];
        if (!isDateAfter(cargoTempLogDate.logDate, fromDate)) return;
        const midnightAfterLogDate = new Date(cargoTempLogDate.logDate);
        midnightAfterLogDate.setHours(24);

        // find all loading stowage items that have a pumping commence event before midnight after the log date
        const loadingStowageItemsUntilToday = activeMasterPlan.stowageItems.filter((stowage) => {
            return (
                +stowage?.cargoOperation?.cargo?.voyageNumber >= firstVoyage &&
                events.some(
                    (event) =>
                        event.eventTypeId === eventType.pumpingCommence &&
                        event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                        stowage.cargoOperation.operationType === 'Loading' &&
                        isDateSameOrBefore(event.eventDate.actualDateTimeLocal, midnightAfterLogDate)
                )
            );
        });
        // get distinct cargoes possibly on board at date from the stowage items
        const loadedCargoes = loadingStowageItemsUntilToday.reduce((acc, stowageItem) => {
            const cargo = activeCargoes.find((cargo) => cargo.cargoGuid === stowageItem.cargoOperation.cargoGuid);
            if (cargo && !acc.some((c) => c.cargoGuid === cargo.cargoGuid)) {
                acc.push(cargo);
            }
            return acc;
        }, [] as Array<RotationCargo>);

        loadedCargoes.forEach((cargo) => {
            const loadingStowage = loadingStowageItemsUntilToday.filter((stowage) => {
                return stowage.cargoOperation.cargoGuid === cargo.cargoGuid;
            });
            const tanks = loadingStowage.map((stowage) => stowage.tankId);

            const tanksNotUnloaded = tanks.filter((tank) => {
                const unloadingStowage = activeMasterPlan.stowageItems.filter((stowage) => {
                    return (
                        stowage.cargoOperation.cargoGuid === cargo.cargoGuid &&
                        stowage.tankId === tank &&
                        stowage.cargoOperation.operationType === 'Unloading' &&
                        events.some(
                            (event) =>
                                unloadCompletionEventTypes.has(event.eventTypeId) &&
                                event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                                isDateBefore(event.eventDate.actualDateTimeLocal, cargoTempLogDate.logDate)
                        )
                    );
                });

                // Transfer operations are vessel-internal and generate no shore-terminal completion events.
                // A negative-weight Transfer stowage item is sufficient proof the cargo left the tank.
                const transferOutStowage = activeMasterPlan.stowageItems.filter((stowage) => {
                    return (
                        stowage.cargoOperation.cargoGuid === cargo.cargoGuid &&
                        stowage.tankId === tank &&
                        stowage.cargoOperation.operationType === 'Transfer' &&
                        new Decimal(stowage.weight).lessThan(0)
                    );
                });

                // No evidence of cargo leaving this tank means it is still on board
                if (unloadingStowage.length === 0 && transferOutStowage.length === 0) return true;

                const loadedQuantity = loadingStowage
                    .filter((stowage) => stowage.tankId === tank)
                    .reduce((acc, stowage) => {
                        const quantity = stowage.weight ?? 0;
                        return acc.add(new Decimal(quantity));
                    }, new Decimal(0));
                const unloadedQuantity = unloadingStowage.reduce((acc, stowage) => {
                    const quantity = stowage.weight ?? 0;
                    return acc.add(new Decimal(quantity));
                }, new Decimal(0));
                const transferredOutQuantity = transferOutStowage.reduce((acc, stowage) => {
                    const quantity = stowage.weight ?? 0;
                    return acc.add(new Decimal(quantity));
                }, new Decimal(0));

                // check if remaining quantity is greater than 0 with a small margin of error
                const marginOfError = 0.01;
                // add load, unload, and transfer because unloading and transfer weights are negative
                const remainingQuantity = loadedQuantity.add(unloadedQuantity).add(transferredOutQuantity).toNumber();
                if (remainingQuantity > marginOfError) return true;

                return false;
            });
            if (tanksNotUnloaded.length === 0) return null;

            cargoInTankItems.push(
                ...tanksNotUnloaded.map((tankId) => {
                    return {
                        cargoNumber: getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, false),
                        isOnBoard: true,
                        tankId: tankId,
                        tradeName: cargo.product.tradeName,
                        cargoTempCategory: getCargoTempCategory(cargo),
                        productId: cargo.productId,
                        chartererName: cargo.chartererName,
                    } as CargoInTankItem;
                })
            );
        });
        cargoTempLogDate.cargoInTankList = cargoInTankItems;
    });
    cargoTempDebugLog('populateCargoOnBoardFromStowage:done', {
        datesWithCargo: cargoTempLogDates.filter((d) => (d.cargoInTankList?.length ?? 0) > 0).length,
        sample: cargoTempLogDates
            .filter((d) => (d.cargoInTankList?.length ?? 0) > 0)
            .slice(0, 3)
            .map((d) => ({
                logDate: d.logDate?.toISOString?.(),
                cargoNumbers: Array.from(new Set((d.cargoInTankList ?? []).map((c) => c.cargoNumber))),
            })),
    });
    return cargoTempLogDates;
};

export const isInhibitorQuestionnaireCompleted = (config: CargoTemperatureConfig): boolean => {
    let isValid = true;
    if (
        config.inhibitorType == null ||
        config.inhibitorAmount == null ||
        config.inhibitorAddedDate == null ||
        config.inhibitorEffectivenessEntries == null ||
        config.inhibitorEffectivenessEntries?.length === 0 ||
        !config.inhibitorEffectivenessEntries.some((entry) => entry.days != null && entry.temperature != null && entry.days > 0) ||
        !config.inhibitorExpiryAction
    )
        isValid = false;

    if (config.isOxygenDependent && (config.minimumOxygenLevel == null || config.minimumOxygenLevel <= 0)) isValid = false;
    return isValid;
};
